import React, { useState, useRef } from 'react';
import { MEETING_TYPES } from '../constants/idleMeetingTypes';

const REASONS = [
  { value: "disputefox", label: "DisputeFox / Platform Work" },
  { value: "training", label: "Training" },
  { value: "meeting", label: "Meeting" },
  { value: "hr", label: "HR / Administrative" },
  { value: "call_with_lead", label: "Call with Lead" },
  { value: "outsourcing", label: "Outsourcing" },
  { value: "other", label: "Other" },
];

const DISPUTEFOX_SUB_CATEGORIES = [
  { value: "onboarding_call", label: "Onboarding call" },
  { value: "billing_outreach", label: "Billing outreach" },
  { value: "customer_service", label: "Customer service" },
  { value: "monthly_one_on_one", label: "Monthly one-on-one" },
  { value: "preparing_dispute_letters", label: "Preparing dispute letters" },
];

const OUTSOURCING_SUB_CATEGORIES = [
  { value: "supply_run", label: "Supply run" },
  { value: "printing_stuffing_letters", label: "Printing and stuffing letters" },
];

// Returns true if the explanation has at least 2 sentences (split on . ! ?) each with >= 2 words.
function hasTwoSentences(text) {
  if (!text || text.trim().length < 20) return false;  // mirror the backend's 20-char floor
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(s => s.split(/\s+/).filter(Boolean).length >= 2);
  return sentences.length >= 2;
}

let _clientIdCounter = 1;
const newClientEntry = () => ({ id: _clientIdCounter++, value: "" });

export default function IdleReasonModal({ onSubmit, idleSince }) {
  const [reason, setReason] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [meetingType, setMeetingType] = useState("");
  const [clientNames, setClientNames] = useState([newClientEntry()]);
  const [explanation, setExplanation] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotName, setScreenshotName] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef();

  const needsScreenshot = reason === "call_with_lead";
  const needsSubCategory = reason === "disputefox" || reason === "outsourcing";
  const needsClientNames = reason === "disputefox" && !!subCategory;

  const addClientName = () => setClientNames(prev => [...prev, newClientEntry()]);
  const removeClientName = (id) => setClientNames(prev => prev.length > 1 ? prev.filter(c => c.id !== id) : prev);
  const updateClientName = (id, val) => setClientNames(prev => prev.map(c => c.id === id ? { ...c, value: val } : c));

  const timeInfo = (() => {
    if (!idleSince) return null;
    const start = new Date(idleSince);
    const today = new Date();
    const isStale = start.toDateString() !== today.toDateString();
    const diffMins = Math.round((Date.now() - start) / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return {
      startStr: start.toLocaleString(),
      durStr: isStale ? null : (hours > 0 ? `${hours}h ${mins}m` : `${diffMins} min`),
      isStale,
    };
  })();

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result);
      setScreenshotName(file.name);
    };
    reader.readAsDataURL(file);
  }

  function handleSubmit() {
    if (!reason) { setError("Please select a reason."); return; }
    if (reason === "meeting" && !meetingType) { setError("Please select the meeting type."); return; }
    if (needsSubCategory && !subCategory) { setError("Please select a sub-category."); return; }
    if (needsClientNames) {
      const filled = clientNames.map(c => c.value.trim()).filter(Boolean);
      if (!filled.length) { setError("Please enter at least one client name."); return; }
    }
    if (!explanation.trim()) { setError("Please describe what you were doing."); return; }
    if (reason === "other") {
      if (explanation.trim().length < 20) { setError("Please provide a more detailed explanation (at least 20 characters)."); return; }
      if (!hasTwoSentences(explanation)) { setError("Please write at least two complete sentences describing what you were doing."); return; }
    }
    if (needsScreenshot && !screenshot) { setError("A screenshot is required for calls with leads."); return; }
    const filledClientNames = needsClientNames ? clientNames.map(c => c.value.trim()).filter(Boolean) : [];
    onSubmit({
      reason,
      sub_category: needsSubCategory ? subCategory : null,
      client_names: filledClientNames.length ? filledClientNames : null,
      meeting_type: reason === "meeting" ? meetingType : null,
      explanation,
      screenshot,
      screenshot_name: screenshotName,
    });
  }

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
  };
  const modal = {
    background: "#1e293b", borderRadius: 12, padding: 28, width: 420,
    maxWidth: "90vw", border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  };
  const label = { color: "#94a3b8", fontSize: 12, fontWeight: 600, marginBottom: 6, display: "block" };
  const input = {
    width: "100%", background: "#0f172a", border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8, padding: "8px 12px", color: "#e2e8f0", fontSize: 13,
    outline: "none", boxSizing: "border-box",
  };
  const btn = {
    padding: "9px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
    background: "#012697", color: "#fff",
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={{ color: "#e2e8f0", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          Idle Period Detected
        </h3>
        <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 14 }}>
          You were inactive. Please log what you were doing during this time.
        </p>

        {timeInfo && (
          <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
            <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>
              Started: <span style={{ color: "#e2e8f0" }}>{timeInfo.startStr}</span>
            </p>
            {timeInfo.isStale ? (
              <p style={{ color: "#f59e0b", fontSize: 12, margin: "4px 0 0" }}>
                Your session ended without closing this period. Enter what you were doing and submit to clear it.
              </p>
            ) : (
              <p style={{ color: "#94a3b8", fontSize: 12, margin: "4px 0 0" }}>
                Duration: <span style={{ color: "#fbbf24", fontWeight: 700 }}>{timeInfo.durStr}</span>
              </p>
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Reason *</label>
          <select
            value={reason}
            onChange={e => { setReason(e.target.value); setSubCategory(""); setClientNames([newClientEntry()]); setError(""); }}
            style={{ ...input, appearance: "none" }}
          >
            <option value="">— Select a reason —</option>
            {REASONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {reason === "meeting" && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Meeting type *</label>
            <select
              value={meetingType}
              onChange={e => { setMeetingType(e.target.value); setError(""); }}
              style={{ ...input, appearance: "none" }}
            >
              <option value="">— Select meeting type —</option>
              {MEETING_TYPES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        )}

        {reason === "disputefox" && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Sub-category *</label>
            <select
              value={subCategory}
              onChange={e => { setSubCategory(e.target.value); setClientNames([newClientEntry()]); setError(""); }}
              style={{ ...input, appearance: "none" }}
            >
              <option value="">— Select sub-category —</option>
              {DISPUTEFOX_SUB_CATEGORIES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        {reason === "outsourcing" && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Sub-category *</label>
            <select
              value={subCategory}
              onChange={e => { setSubCategory(e.target.value); setError(""); }}
              style={{ ...input, appearance: "none" }}
            >
              <option value="">— Select sub-category —</option>
              {OUTSOURCING_SUB_CATEGORIES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        {needsClientNames && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Client name(s) *</label>
            {clientNames.map((entry, idx) => (
              <div key={entry.id} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input
                  type="text"
                  value={entry.value}
                  onChange={e => { updateClientName(entry.id, e.target.value); setError(""); }}
                  placeholder={`Client ${idx + 1} name`}
                  style={{ ...input, flex: 1 }}
                />
                {clientNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeClientName(entry.id)}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#94a3b8", padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addClientName}
              style={{ background: "none", border: "none", color: "#59a52c", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, marginTop: 2 }}
            >
              + Add another client
            </button>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={label}>What were you doing? *</label>
          <textarea
            rows={3}
            value={explanation}
            onChange={e => { setExplanation(e.target.value); setError(""); }}
            placeholder={reason === "other" ? "Describe in at least two complete sentences what you were doing..." : "Describe specifically what you were working on..."}
            style={{ ...input, resize: "vertical" }}
          />
          {reason === "other" && (
            <p style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>
              Required: at least two sentences (&ge;20 characters total).
            </p>
          )}
        </div>

        {needsScreenshot && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Screenshot of call *</label>
            <input type="file" accept="image/*" ref={fileRef} onChange={handleFile} style={{ display: "none" }} />
            <button
              onClick={() => fileRef.current.click()}
              style={{ ...btn, width: "100%", textAlign: "left", background: "rgba(255,255,255,0.08)", color: "#94a3b8" }}
            >
              {screenshotName || "Choose screenshot…"}
            </button>
          </div>
        )}

        {error && (
          <p style={{ color: "#ef4444", fontSize: 12, marginBottom: 12 }}>{error}</p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button style={btn} onClick={handleSubmit}>Submit & Continue</button>
        </div>
      </div>
    </div>
  );
}
